import React, { useCallback } from 'react';
import Container from 'atomic/container';
import ProductBoxNarrow from 'modules/shared/components/product/product-box-narrow';
import MainLayoutDesktop from 'layouts/main-layout/main-layout.desktop';
import { useSelector, withHydrate } from '@ecommerce/nest-ssr/common';
import BreadcrumbsDesktop from 'modules/shared/components/breadcrumbs/breadcrumbs.desktop';
import MainTitle from 'modules/shared/components/main-title';
import ProductViewType from 'modules/categories/views/components/product-view-type';
import reducers from 'modules/categories/reducers';
import ProductBoxWideDesktop from 'modules/shared/components/product/product-box-wide/product-box-wide-desktop';
import { ListingType, ListingTypesEnum } from 'modules/shared/modules/categories-shared/interfaces/category-listing-config.interface';
import PageDetails from 'modules/categories/views/components/page-details';
import PaginationMini from 'components/paginations/pagination-mini';
import { useSearchUrl } from 'modules/search/hooks/use-search-url';
import Pagination from 'components/paginations/pagination';
import { Head } from '@ecommerce/nest-ssr/components';
import SearchNoResult from 'modules/search/views/components/search-no-result';
import { SearchPageProps } from 'modules/search/interfaces/search-page.props';
import getCountMax from 'helpers/get-count-max';
import { useSeo } from 'hooks/use-seo';
import Newsletter from 'components/newsletter';
import SearchStructuredData from 'modules/search/views/components/search-structured-data';
import Dropdown from 'atomic/dropdown/dropdown';

import './search.desktop.scss';

const PAGE_PRODUCTS_LIMIT = 20;

const SearchDesktop: React.FC<SearchPageProps> = ({ products, breadcrumbs, query, params, category }) => {
    const seo = useSeo();
    const getUrl = useSearchUrl();
    const listingType = useSelector((state: { listingType: ListingType; }) => state.listingType ?? ListingTypesEnum.Grid);
    const getPaginationUrl = useCallback((page: number) => getUrl({ query: params.query, page, sort: params.sort, categoryId: params.categoryId }), [getUrl]);

    const onSelect = (value: string): void => {
        window.location.replace(getUrl({ query: params.query, sort: value, page: 1, categoryId: params.categoryId }));
    };

    const sortList = [
        { value: '', label: 'Popularność' },
        { value: 'price-desc', label: 'Cena malejąco' },
        { value: 'price-asc', label: 'Cena rosnąco' },
        { value: 'rating', label: 'Najlepsze opinie' },
        { value: 'most-offers', label: 'Najwięcej ofert' },
        { value: 'latest', label: 'Najnowsze' }
    ];

    if (params.showDiscounts) {
        sortList.push({ value: 'discount', label: 'Obniżka' });
    }

    return (
        <MainLayoutDesktop>
            <Head>
                <meta
                    name="robots"
                    content={seo.robotsContent}
                />
            </Head>
            <Container className="search-page">
                <div className="row">
                    <div className="col col-12">
                        <BreadcrumbsDesktop breadcrumbs={breadcrumbs} />
                    </div>
                    <div className="col col-12">
                        <MainTitle text={query}>
                            ({getCountMax(products.count)})
                        </MainTitle>
                    </div>
                </div>
                {products.count === 0
                    ? (
                        <SearchNoResult query={params.query} categoryName={category?.name} />
                    )
                    : (
                        <>
                            <div className="row search-page__header">
                                <div className="col col-6 search-page__header--left">
                                    <ProductViewType pageTypeName={query} />
                                    <PageDetails
                                        page={params.page}
                                        count={products.count}
                                        listingCount={PAGE_PRODUCTS_LIMIT}
                                    />
                                </div>
                                <div className="col col-6 search-page__header--right">
                                    <Dropdown
                                        className="search-sort"
                                        list={sortList}
                                        defaultValue={params.sort}
                                        onChange={onSelect}
                                        label="Sortuj:"
                                        hideSelectedItems={true}
                                        isTransparent={true}
                                    />
                                    <PaginationMini
                                        page={params.page}
                                        count={products.count}
                                        limit={PAGE_PRODUCTS_LIMIT}
                                        getUrl={getPaginationUrl}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                {
                                    products.items.map((product) => (
                                        <>
                                            {listingType === ListingTypesEnum.Grid && (
                                                <div className="col product-item" key={product.id}>
                                                    <ProductBoxNarrow product={product} />
                                                </div>
                                            )}
                                            {listingType === ListingTypesEnum.List && (
                                                <div className="col col-12" key={product.id}>
                                                    <ProductBoxWideDesktop product={product} />
                                                </div>
                                            )}
                                        </>
                                    ))
                                }
                            </div>
                            <div className="row">
                                <div className="col  col-12">
                                    <Pagination
                                        page={params.page}
                                        count={products.count}
                                        limit={PAGE_PRODUCTS_LIMIT}
                                        getUrl={getPaginationUrl}
                                    />
                                </div>
                            </div>
                        </>
                    )
                }
            </Container>
            <SearchStructuredData
                title={query}
                page={params.page}
                products={products}
                pageLimit={PAGE_PRODUCTS_LIMIT}
            />
            <Newsletter isMobile={false} />
        </MainLayoutDesktop>
    );
};

export default withHydrate(SearchDesktop, { root: '#root', reducers }, 'modules/search/views/pages/search.desktop');
